import React, { useEffect, useMemo, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { staggerContainer, itemVariants, subtleItemVariants } from './dashboardMotion';
import { database } from '../firebaseConfig';
import { ref, onValue, update, serverTimestamp } from 'firebase/database';
import {
  reportingPeriods,
  reportingState,
  reportingId,
  paymentId,
  payoutId,
  extraFromQualified,
  includedAppointmentsFor,
  appointmentPriceFor,
  commissionForMember,
  fmtEur,
  fmtDate,
  monthLabel,
} from './paymentEngine';
import { computeProjection } from '../Research/pricingEngine';
import reportingMail from './reportingMailCore';
import { buildExtraApptUpdates, REPORTINGS_PATH } from './reportingSync';
import './PaymentDashboards.css';
import './ReportingDashboard.css';

const CUSTOMERS_PATH = 'salesgod-research/customers';
const TEAM_PATH = 'salesgod-research/team';
const PAYMENTS_PATH = 'salesgod-research/payments';
const PAYOUTS_PATH = 'salesgod-research/payouts';
const REPORTS_PATH = 'salesgod-research/reports';

const fmtInvoiceNo = (n) => (typeof n === 'number' ? `RE-${n}` : null);

const FILTERS = [
  { id: 'todo', label: 'Zu erledigen' },
  { id: 'countdown', label: 'Countdown' },
  { id: 'submitted', label: 'Eingereicht' },
  { id: 'all', label: 'Alle' },
];

// Verbleibende Zeit als kompakter Countdown ("3 T 4 Std" / "5 Std 12 Min" / "fällig").
const fmtCountdown = (targetMs, now) => {
  const diff = targetMs - now;
  if (diff <= 0) return null;
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (d > 0) return `${d} T ${h} Std`;
  if (h > 0) return `${h} Std ${m} Min`;
  return `${m} Min`;
};

const ReportingDashboard = ({ embedded = false, readOnly = false } = {}) => {
  const [customers, setCustomers] = useState({});
  const [team, setTeam] = useState({});
  const [payments, setPayments] = useState({});
  const [payouts, setPayouts] = useState({});
  const [reportings, setReportings] = useState({});
  const [reports, setReports] = useState({});
  const [loadingC, setLoadingC] = useState(true);
  const [loadingR, setLoadingR] = useState(true);
  const [now, setNow] = useState(Date.now());
  const [filter, setFilter] = useState('todo');
  const [formRep, setFormRep] = useState(null); // { id, ... , qualified, summary, reportUrl }
  const [saving, setSaving] = useState(false);
  const genRef = useRef(false);

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const unsub = onValue(ref(database, CUSTOMERS_PATH), (snap) => { setCustomers(snap.val() || {}); setLoadingC(false); });
    return () => unsub();
  }, []);
  useEffect(() => {
    const unsub = onValue(ref(database, TEAM_PATH), (snap) => setTeam(snap.val() || {}));
    return () => unsub();
  }, []);
  useEffect(() => {
    const unsub = onValue(ref(database, PAYMENTS_PATH), (snap) => setPayments(snap.val() || {}));
    return () => unsub();
  }, []);
  useEffect(() => {
    const unsub = onValue(ref(database, PAYOUTS_PATH), (snap) => setPayouts(snap.val() || {}));
    return () => unsub();
  }, []);
  useEffect(() => {
    const unsub = onValue(ref(database, `salesgod-research/reportings`), (snap) => { setReportings(snap.val() || {}); setLoadingR(false); });
    return () => unsub();
  }, []);
  useEffect(() => {
    const unsub = onValue(ref(database, REPORTS_PATH), (snap) => setReports(snap.val() || {}));
    return () => unsub();
  }, []);

  // Termin-Preis (netto) je Kunde – Vertrag schlägt Sales Research.
  const apptPrice = (slug) => {
    const contract = slug && customers[slug] && customers[slug].contract;
    const own = appointmentPriceFor(contract, 0);
    if (own > 0) return Math.round(own);
    const cfg = slug && reports[slug] && reports[slug].config;
    if (!cfg) return 0;
    try {
      const v = computeProjection(cfg).appointmentValue;
      return typeof v === 'number' && v > 0 ? Math.round(v) : 0;
    } catch (e) { return 0; }
  };

  // Im Retainer enthaltene Termine dieser Periode. Der auf dem Reporting
  // gespeicherte Wert gewinnt (Historie), sonst der aktuelle Vertrag.
  // Achtung: 0 ist ein gültiger Wert (reines Provisionsmodell).
  const includedFor = (rep) => {
    if (rep && typeof rep.included === 'number') return rep.included;
    const contract = rep && customers[rep.customerSlug] && customers[rep.customerSlug].contract;
    return includedAppointmentsFor(contract);
  };

  // Generator: legt für jede fällige (+ die nächste anstehende) Reporting-Periode
  // ein Objekt an. Idempotent über die deterministische ID slug__periodKey.
  useEffect(() => {
    if (loadingC || loadingR || genRef.current) return;
    const toCreate = [];
    Object.keys(customers).forEach((slug) => {
      const c = customers[slug] || {};
      reportingPeriods(c.contract, now).forEach((p) => {
        const id = reportingId(slug, p.periodKey);
        if (!reportings[id]) toCreate.push({ id, slug, c, p });
      });
    });
    if (!toCreate.length) return;
    genRef.current = true;
    (async () => {
      try {
        const updates = {};
        toCreate.forEach(({ id, slug, c, p }) => {
          const rec = {
            customerSlug: slug,
            companyName: c.companyName || slug,
            periodKey: p.periodKey,
            availableAt: p.availableAt,
            dueDate: p.dueDate,
            periodStart: p.periodStart,
            periodEnd: p.periodEnd,
            deadlineAt: p.deadlineAt,
            included: p.included,
            status: p.availableAt > now ? 'scheduled' : 'open',
            createdAt: serverTimestamp(),
            source: 'auto',
          };
          if (typeof c.customerId === 'number') rec.customerId = c.customerId;
          updates[`${REPORTINGS_PATH}/${id}`] = rec;
        });
        if (Object.keys(updates).length) await update(ref(database), updates);
      } catch (e) {
        console.error('Reportings erzeugen fehlgeschlagen:', e);
      } finally {
        genRef.current = false;
      }
    })();
  }, [loadingC, loadingR, customers, reportings, now]);

  const list = useMemo(() =>
    Object.keys(reportings)
      .map((id) => ({ id, ...reportings[id], state: reportingState(reportings[id], now) }))
      .sort((a, b) => (b.availableAt || 0) - (a.availableAt || 0)),
  [reportings, now]);

  const counts = useMemo(() => ({
    todo: list.filter((r) => r.state === 'open' || r.state === 'overdue').length,
    countdown: list.filter((r) => r.state === 'scheduled').length,
    submitted: list.filter((r) => r.state === 'submitted').length,
    all: list.length,
  }), [list]);

  const filtered = useMemo(() => {
    if (filter === 'all') return list;
    if (filter === 'todo') return list.filter((r) => r.state === 'open' || r.state === 'overdue');
    if (filter === 'countdown') return list.filter((r) => r.state === 'scheduled');
    return list.filter((r) => r.state === 'submitted');
  }, [list, filter]);

  const kpis = useMemo(() => {
    const todo = list.filter((r) => r.state === 'open' || r.state === 'overdue');
    const overdue = list.filter((r) => r.state === 'overdue').length;
    const nextUp = list
      .filter((r) => r.state === 'scheduled')
      .sort((a, b) => (a.availableAt || 0) - (b.availableAt || 0))[0];
    const extrasThisCycle = list
      .filter((r) => r.state === 'submitted')
      .reduce((s, r) => s + (Number(r.extraAppointments) || 0), 0);
    return { todoCount: todo.length, overdue, nextUp, extrasThisCycle };
  }, [list]);

  // Für eine Karte die finanzielle Wirkung der Extra-Termine berechnen.
  const impactFor = (rep, extra) => {
    const ex = Math.max(0, Math.round(Number(extra)) || 0);
    const price = (() => {
      const pid = paymentId(rep.customerSlug, rep.periodKey);
      const pay = payments[pid];
      if (pay && pay.appointmentPrice > 0) return pay.appointmentPrice;
      return apptPrice(rep.customerSlug);
    })();
    const cust = customers[rep.customerSlug] || {};
    const contract = cust.contract || null;
    const assigned = cust.team || {};
    const qualified = ex + includedFor(rep);
    // Kosten der Termine: gestaffelte Provision, wo eine Staffel greift –
    // sonst wie bisher der pauschale Bonus je Extra-Termin.
    let payoutPlus = 0;
    Object.keys(assigned).forEach((mid) => {
      const share = commissionForMember(team[mid], contract, qualified);
      if (share) { payoutPlus += share.amount; return; }
      const po = payouts[payoutId(mid, rep.customerSlug, rep.periodKey)];
      const b = (po && typeof po.appointmentBonus === 'number') ? po.appointmentBonus : Number((team[mid] || {}).appointmentBonus) || 0;
      payoutPlus += ex * b;
    });
    return { ex, invoicePlus: ex * price, payoutPlus };
  };

  const openForm = (rep) => {
    const included = includedFor(rep);
    const q = typeof rep.qualifiedAppointments === 'number' ? rep.qualifiedAppointments : included;
    setFormRep({ ...rep, qualified: String(q), summary: rep.summary || '', reportUrl: rep.reportUrl || '' });
  };
  const closeForm = () => { if (!saving) setFormRep(null); };

  const submitReporting = async () => {
    if (!formRep) return;
    const included = includedFor(formRep);
    const q = Math.max(0, Math.round(Number(formRep.qualified)) || 0);
    const extra = extraFromQualified(q, included);
    setSaving(true);
    try {
      const updates = buildExtraApptUpdates({
        slug: formRep.customerSlug,
        periodKey: formRep.periodKey,
        extra,
        qualified: q,
        customers, team, payments, payouts, reportings,
        apptPrice,
      });
      const base = `${REPORTINGS_PATH}/${formRep.id}`;
      // Tatsächlich eingegebene Zahl gewinnt (kann unter der inkludierten Menge liegen).
      updates[`${base}/qualifiedAppointments`] = q;
      updates[`${base}/extraAppointments`] = extra;
      updates[`${base}/included`] = included;
      updates[`${base}/status`] = 'submitted';
      updates[`${base}/summary`] = (formRep.summary || '').trim();
      if ((formRep.reportUrl || '').trim()) updates[`${base}/reportUrl`] = formRep.reportUrl.trim();
      updates[`${base}/submittedAt`] = Date.now();
      updates[`${base}/updatedAt`] = Date.now();
      await update(ref(database), updates);
      setFormRep(null);
    } catch (e) {
      console.error('Reporting speichern fehlgeschlagen:', e);
      window.alert('Speichern fehlgeschlagen: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  // Live-Vorschau im Formular.
  const formIncluded = formRep ? includedFor(formRep) : 0;
  const formExtra = formRep ? extraFromQualified(formRep.qualified, formIncluded) : 0;
  const formImpact = formRep ? impactFor(formRep, formExtra) : null;
  const formInvoiceBilled = formRep ? (() => {
    const pay = payments[paymentId(formRep.customerSlug, formRep.periodKey)];
    return pay && pay.status && pay.status !== 'due';
  })() : false;

  const loading = loadingC || loadingR;

  const stateBadge = (state) => {
    if (state === 'submitted') return { c: 'submitted', t: 'eingereicht' };
    if (state === 'open') return { c: 'open', t: 'offen' };
    if (state === 'overdue') return { c: 'overdue', t: 'überfällig' };
    return { c: 'scheduled', t: 'Countdown' };
  };

  // Was ist per Mail passiert bzw. warum passiert nichts? Empfänger werden pro
  // Kunde in der Sales Pipeline gepflegt (Deal & Team → Reporting-Erinnerungen).
  const reminderInfo = (r) => {
    const count = Math.max(0, Math.round(Number(r.reminderCount) || 0));
    if (r.state === 'submitted') {
      return r.confirmationSentAt
        ? { tone: 'ok', text: `Bestätigung ${fmtDate(r.confirmationSentAt)} verschickt${count ? ` · ${count}× erinnert` : ''}` }
        : null;
    }
    if (r.state !== 'open' && r.state !== 'overdue') return null;
    if (count > 0) {
      const admin = r.adminNotifiedAt ? ' · Admin informiert' : '';
      return { tone: 'due', text: `${count}× erinnert, letzte ${fmtDate(r.lastReminderAt)}${admin}` };
    }
    const customer = customers[r.customerSlug];
    if (!customer) return null;
    const recipients = reportingMail.resolveRecipients(customer, team);
    if (!recipients.enabled) return { tone: 'warn', text: 'Erinnerungen für diesen Kunden ausgeschaltet' };
    if (!recipients.to.length) return { tone: 'warn', text: 'Keine Erinnerung möglich – kein Empfänger hinterlegt' };
    return { tone: 'idle', text: `Erinnerung geht an ${recipients.to.join(', ')}` };
  };

  const content = (
    <>
        {!embedded && (
        <motion.header className="pay-head" variants={itemVariants}>
          <div className="pay-head-text">
            <h1>Reporting</h1>
            <p>Monatliche Outreach-Reportings – automatisch im Rechnungstakt. Nach jedem 30-Tage-Zyklus schaltet der Report frei, der Outreach-Manager trägt sein Fazit und die qualifizierten Termine ein. Extra-Termine fließen automatisch in Rechnung und Team-Auszahlung.</p>
          </div>
        </motion.header>
        )}

        {embedded && (
          <motion.p className="rep-embedded-hint" variants={itemVariants}>
            Übersicht aller monatlichen Reportings. Eingereicht wird über die Research-Seite des jeweiligen Kontakts – die qualifizierten Termine fließen automatisch in Rechnung und Team-Auszahlung.
          </motion.p>
        )}

        <motion.div className="pay-kpis" variants={staggerContainer}>
          <motion.div className={`pay-kpi ${kpis.todoCount ? 'pay-kpi-amber' : ''}`} variants={subtleItemVariants}>
            <div className="pay-kpi-val">{kpis.todoCount}</div>
            <div className="pay-kpi-lbl">Zu erledigen</div>
            <div className="pay-kpi-sub">{kpis.overdue ? `${kpis.overdue} überfällig` : 'Im 7-Tage-Fenster'}</div>
          </motion.div>
          <motion.div className="pay-kpi" variants={subtleItemVariants}>
            <div className="pay-kpi-val">{kpis.nextUp ? (fmtCountdown(kpis.nextUp.availableAt, now) || 'jetzt') : '–'}</div>
            <div className="pay-kpi-lbl">Nächstes Reporting</div>
            <div className="pay-kpi-sub">{kpis.nextUp ? `${kpis.nextUp.companyName} · ${monthLabel(kpis.nextUp.periodKey)}` : 'Kein anstehendes'}</div>
          </motion.div>
          <motion.div className="pay-kpi pay-kpi-accent" variants={subtleItemVariants}>
            <div className="pay-kpi-val">{kpis.extrasThisCycle}</div>
            <div className="pay-kpi-lbl">Erfasste Extra-Termine</div>
            <div className="pay-kpi-sub">Aus eingereichten Reportings</div>
          </motion.div>
        </motion.div>

        {loading ? (
          <div className="pay-empty">Reportings werden geladen …</div>
        ) : (
          <>
            <motion.div className="pay-filters" variants={itemVariants}>
              {FILTERS.map((f) => (
                <button key={f.id} type="button" className={`pay-filter ${filter === f.id ? 'pay-filter-active' : ''}`} onClick={() => setFilter(f.id)}>
                  {f.label}<span className="pay-filter-count">{counts[f.id]}</span>
                </button>
              ))}
            </motion.div>

            {filtered.length === 0 ? (
              <div className="pay-empty">
                {filter === 'todo'
                  ? 'Aktuell keine Reportings offen. Sobald ein 30-Tage-Zyklus eines aktiven Vertrags endet, schaltet hier automatisch ein Report frei – zeitgleich mit der Rechnung.'
                  : filter === 'countdown'
                  ? 'Kein laufender Countdown. Sobald du bei einem Kunden den Vertrag aktivierst, startet hier automatisch der 30-Tage-Countdown bis zum ersten Reporting.'
                  : filter === 'submitted'
                  ? 'Noch keine eingereichten Reportings.'
                  : 'Noch keine Reportings vorhanden.'}
              </div>
            ) : (
              <motion.div className="pay-list" variants={staggerContainer}>
                {filtered.map((r) => {
                  const badge = stateBadge(r.state);
                  const pay = payments[paymentId(r.customerSlug, r.periodKey)];
                  const extra = typeof r.extraAppointments === 'number'
                    ? r.extraAppointments
                    : (typeof r.qualifiedAppointments === 'number' ? extraFromQualified(r.qualifiedAppointments, includedFor(r)) : 0);
                  const impact = impactFor(r, extra);
                  const countdownTarget = r.state === 'scheduled' ? r.availableAt : (r.state === 'open' ? r.deadlineAt : null);
                  const countdown = countdownTarget ? fmtCountdown(countdownTarget, now) : null;
                  return (
                    <motion.div key={r.id} variants={subtleItemVariants} className={`pay-row rep-row rep-row-${r.state}`}>
                      <div className="pay-row-main">
                        <span className="pay-row-name">
                          {r.companyName}
                          <span className="rep-period">{monthLabel(r.periodKey)}</span>
                          {pay && typeof pay.invoiceNo === 'number' && <span className="pay-invno">{fmtInvoiceNo(pay.invoiceNo)}</span>}
                        </span>
                        <span className="pay-row-meta">
                          {r.state === 'scheduled' && `Zeitraum ${fmtDate(r.periodStart)} – ${fmtDate(r.periodEnd)} · schaltet frei ${fmtDate(r.availableAt)}`}
                          {r.state === 'open' && `Freigeschaltet ${fmtDate(r.availableAt)} · Frist ${fmtDate(r.deadlineAt)}`}
                          {r.state === 'overdue' && `Frist ${fmtDate(r.deadlineAt)} überschritten – bitte nachtragen`}
                          {r.state === 'submitted' && `Eingereicht ${fmtDate(r.submittedAt)} · ${r.qualifiedAppointments || 0} qualifizierte Termine`}
                        </span>
                        {r.state === 'submitted' && r.summary && <span className="rep-summary">{r.summary}</span>}
                        {(extra > 0) && (
                          <span className="rep-impact">
                            +{extra} Extra-Termin{extra === 1 ? '' : 'e'}
                            {impact.invoicePlus > 0 && ` · Rechnung +${fmtEur(impact.invoicePlus)}`}
                            {impact.payoutPlus > 0 && ` · Team +${fmtEur(impact.payoutPlus)}`}
                          </span>
                        )}
                        {(() => {
                          const info = reminderInfo(r);
                          return info ? <span className={`rep-mail rep-mail-${info.tone}`}>{info.text}</span> : null;
                        })()}
                      </div>

                      {countdown && (
                        <div className={`rep-countdown ${r.state === 'open' ? 'rep-countdown-open' : ''}`}>
                          <div className="rep-countdown-val">{countdown}</div>
                          <div className="rep-countdown-lbl">{r.state === 'scheduled' ? 'bis Freischaltung' : 'bis Fristende'}</div>
                        </div>
                      )}

                      <span className={`pay-badge rep-badge-${badge.c}`}>{badge.t}</span>

                      <div className="pay-row-actions">
                        {r.reportUrl && <a className="pay-icon-link" href={r.reportUrl} target="_blank" rel="noreferrer" title="Report öffnen">↗</a>}
                        {readOnly ? (
                          (r.state === 'open' || r.state === 'overdue')
                            ? <span className="rep-locked" title="Wird über die Research-Seite des Kontakts eingereicht">über Research-Seite</span>
                            : r.state === 'scheduled'
                              ? <span className="rep-locked" title="Reporting ist erst nach Ablauf des 30-Tage-Zyklus editierbar">gesperrt</span>
                              : null
                        ) : (
                          <>
                            {(r.state === 'open' || r.state === 'overdue') && (
                              <button type="button" className="pay-btn pay-btn-primary" onClick={() => openForm(r)}>Reporting ausfüllen</button>
                            )}
                            {r.state === 'submitted' && (
                              <button type="button" className="pay-btn pay-btn-ghost" onClick={() => openForm(r)}>Bearbeiten</button>
                            )}
                            {r.state === 'scheduled' && (
                              <span className="rep-locked" title="Reporting ist erst nach Ablauf des 30-Tage-Zyklus editierbar">gesperrt</span>
                            )}
                          </>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </motion.div>
            )}
          </>
        )}
    </>
  );

  const modal = !readOnly && formRep && (
        <div className="pay-modal-overlay" onMouseDown={closeForm}>
          <motion.div
            className="pay-modal rep-modal"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className="pay-modal-head">
              <h2>Reporting · {formRep.companyName}</h2>
              <button type="button" className="pay-modal-close" onClick={closeForm} aria-label="Schließen">✕</button>
            </div>
            <div className="pay-modal-body">
              <p className="rep-modal-sub">{monthLabel(formRep.periodKey)} · Zeitraum {fmtDate(formRep.periodStart)} – {fmtDate(formRep.periodEnd)}</p>

              <label className="pay-field">
                <span className="pay-field-lbl">Qualifizierte Termine in diesem Zeitraum</span>
                <input
                  className="pay-input"
                  type="number"
                  min="0"
                  inputMode="numeric"
                  value={formRep.qualified}
                  onChange={(e) => setFormRep((f) => ({ ...f, qualified: e.target.value }))}
                />
              </label>

              <div className="rep-preview">
                <div className="rep-preview-row">
                  <span>{formIncluded} Termine inklusive</span>
                  <strong>{formExtra} Extra-Termin{formExtra === 1 ? '' : 'e'}</strong>
                </div>
                {formExtra > 0 && (formImpact.invoicePlus > 0 || formImpact.payoutPlus > 0) && (
                  <div className="rep-preview-impact">
                    {formImpact.invoicePlus > 0 && <span>Rechnung +{fmtEur(formImpact.invoicePlus)}</span>}
                    {formImpact.payoutPlus > 0 && <span>Team-Auszahlung +{fmtEur(formImpact.payoutPlus)}</span>}
                  </div>
                )}
                {formExtra > 0 && formInvoiceBilled && (
                  <div className="rep-preview-warn">Achtung: Die Rechnung für diesen Monat ist bereits gestellt – die Extra-Termine werden im Report erfasst, ändern den bereits gestellten Rechnungsbetrag aber nicht mehr.</div>
                )}
              </div>

              <label className="pay-field">
                <span className="pay-field-lbl">Fazit / Zusammenfassung der letzten 30 Tage</span>
                <textarea
                  className="pay-input pay-textarea"
                  rows={5}
                  value={formRep.summary}
                  placeholder="Wins, Herausforderungen, Learnings, Empfehlungen …"
                  onChange={(e) => setFormRep((f) => ({ ...f, summary: e.target.value }))}
                />
              </label>

              <label className="pay-field">
                <span className="pay-field-lbl">Report-Link (optional)</span>
                <input
                  className="pay-input"
                  type="url"
                  value={formRep.reportUrl}
                  placeholder="https://…"
                  onChange={(e) => setFormRep((f) => ({ ...f, reportUrl: e.target.value }))}
                />
              </label>
            </div>

            <div className="pay-modal-actions">
              <button type="button" className="pay-btn pay-btn-ghost" onClick={closeForm} disabled={saving}>Abbrechen</button>
              <button type="button" className="pay-btn pay-btn-primary" onClick={submitReporting} disabled={saving}>
                {saving ? 'Speichern …' : 'Reporting einreichen'}
              </button>
            </div>
          </motion.div>
        </div>
  );

  if (embedded) {
    return (
      <div className="rep-embedded">
        <motion.div className="rep-embedded-inner" variants={staggerContainer} initial="initial" animate="animate">
          {content}
        </motion.div>
        {modal}
      </div>
    );
  }

  return (
    <div className="pay-wrap">
      <motion.div className="pay-inner" variants={staggerContainer} initial="initial" animate="animate">
        {content}
      </motion.div>
      {modal}
    </div>
  );
};

export default ReportingDashboard;
